import React, { useEffect, useState, useRef } from 'react';
import { useApi } from '../hooks/useApi';

// Visual icon for an event row. Plain JSX/SVG — no extra deps.
function EventIcon({ event }) {
  if (event.type === 'goal') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }} title={event.subtype || 'Goal'}>
        <span style={{ fontSize: '0.85rem' }} aria-hidden="true">⚽</span>
        {event.penaltyKick && <span style={{ fontSize: '0.6rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>(P)</span>}
        {event.ownGoal && <span style={{ fontSize: '0.6rem', color: 'var(--danger)', fontFamily: 'var(--font-mono)' }}>(OG)</span>}
      </span>
    );
  }
  if (event.type === 'yellow-card') {
    return <span style={{ display: 'inline-block', width: 9, height: 12, background: '#f7d000', borderRadius: 1 }} aria-label="Yellow card" title="Yellow card" />;
  }
  if (event.type === 'red-card') {
    return <span style={{ display: 'inline-block', width: 9, height: 12, background: 'var(--danger)', borderRadius: 1 }} aria-label="Red card" title="Red card" />;
  }
  return null;
}

function EventRow({ event }) {
  const isHome = event.team === 'home';
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      justifyContent: isHome ? 'flex-start' : 'flex-end',
      padding: '0.25rem 0',
      fontFamily: 'var(--font-mono)',
      fontSize: 'var(--t-xs)',
    }}>
      {isHome ? (
        <>
          <span style={{ minWidth: 36, color: 'var(--text-muted)' }}>{event.minute}</span>
          <EventIcon event={event} />
          <span>{event.player}</span>
        </>
      ) : (
        <>
          <span style={{ textAlign: 'right' }}>{event.player}</span>
          <EventIcon event={event} />
          <span style={{ minWidth: 36, color: 'var(--text-muted)', textAlign: 'right' }}>{event.minute}</span>
        </>
      )}
    </div>
  );
}

export default function EventTimeline({ matchId, live }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const fetchOnce = () => {
      api.getMatchEvents(matchId)
        .then(d => { if (!cancelled) { setData(d); setError(null); } })
        .catch(err => {
          if (cancelled) return;
          // 404 means "no events yet" — render nothing, not an error
          if (err.message?.includes('No events')) setData({ events: [] });
          else setError(err.message);
        });
    };

    fetchOnce();
    if (live) pollRef.current = setInterval(fetchOnce, 60_000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [matchId, live]); // eslint-disable-line

  if (error)              return null;
  if (data === null)      return null;
  const events = data.events || [];
  if (events.length === 0) return null;

  return (
    <div className="modal-pred-section" style={{ paddingTop: '1rem', paddingBottom: '0.5rem' }}>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--t-xs)',
        color: 'var(--volt)',
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        marginBottom: '0.5rem',
      }}>
        Match events
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {events.map((e, i) => <EventRow key={i} event={e} />)}
      </div>
      {(data.attendance || data.referee) && (
        <div style={{
          marginTop: '0.75rem',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--t-xs)',
          color: 'var(--text-faint)',
          letterSpacing: '.04em',
        }}>
          {data.attendance && <span>Attendance: {Number(data.attendance).toLocaleString()}</span>}
          {data.attendance && data.referee && <span> · </span>}
          {data.referee && <span>Referee: {data.referee}</span>}
        </div>
      )}
    </div>
  );
}
